import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import {
    appClockServiceProvider,
    checkIsRunning,
    commanderContextProvider,
    featureFlagServiceProvider,
    getCaptchaService,
    getGainsightService,
    getInitChain,
    getPciProxyService,
    getReactQueryClient,
    store as getStore,
    graphqlClientConfigProvider,
    intlLocalizationProvider,
    localizationHandlers,
    localizationProvider,
    loggerServiceProvider,
    repositoryRegistryProvider,
    routeResolveProvider,
} from '@mews-commander/legacy-app-instance';
import {
    CommanderBreakpointRangeProvider,
    COMPONENT_TYPE,
    ComponentType,
    DefaultErrorBoundary,
    OptimusProvider,
    QueryBoundary,
    REACT_COMPONENT_DATA,
    SuspenseBoundary,
} from '@mews-commander/ui';

import {
    CaptchaServiceContextProvider,
    GainsightServiceProvider,
    logSentryError,
    PciProxyContextProvider,
    WindowSizeProvider,
} from '@mews/framework';
import { GlobalizationProvider } from './components/providers/GlobalizationProvider';
import { CommanderObservability } from './services/CommanderObservability';

export async function renderReactNodeWithContext(
    node: React.ReactElement,
    rootElement: HTMLElement,
    { showErrorAtBoundary = true } = {}
) {
    await getInitChainWithCheck();

    const store = getStore();
    const LocalizationProvider = localizationProvider();
    const CommanderContextProvider = commanderContextProvider();
    const FeatureFlagServiceProvider = featureFlagServiceProvider();
    const LoggerServiceProvider = loggerServiceProvider();
    const AppClockServiceProvider = appClockServiceProvider();
    const RouteResolveProvider = routeResolveProvider();
    const RepositoryRegistryProvider = repositoryRegistryProvider();
    const GraphqlClientConfigProvider = graphqlClientConfigProvider();
    const reactQueryClient = getReactQueryClient();
    const gainsightService = getGainsightService();
    const IntlLocalizationProvider = intlLocalizationProvider();
    const pciProxyService = getPciProxyService();
    const captchaService = getCaptchaService();

    ReactDOM.render(
        <Provider store={store}>
            <FeatureFlagServiceProvider>
                <LoggerServiceProvider>
                    <OptimusProvider>
                        <SuspenseBoundary loader="overlay">
                            <CommanderObservability>
                                <DefaultErrorBoundary showError={showErrorAtBoundary}>
                                    <IntlLocalizationProvider>
                                        <QueryBoundary loader="overlay" showError={showErrorAtBoundary}>
                                            <GlobalizationProvider>
                                                <LocalizationProvider localizationHandlers={localizationHandlers()}>
                                                    <RouteResolveProvider>
                                                        <CommanderContextProvider>
                                                            <WindowSizeProvider>
                                                                <CommanderBreakpointRangeProvider>
                                                                    <AppClockServiceProvider>
                                                                        <RepositoryRegistryProvider>
                                                                            <GraphqlClientConfigProvider>
                                                                                <QueryClientProvider
                                                                                    client={reactQueryClient}
                                                                                >
                                                                                    <GainsightServiceProvider
                                                                                        gainsightService={
                                                                                            gainsightService
                                                                                        }
                                                                                    >
                                                                                        <PciProxyContextProvider
                                                                                            value={pciProxyService}
                                                                                        >
                                                                                            <CaptchaServiceContextProvider
                                                                                                value={captchaService}
                                                                                            >
                                                                                                {node}
                                                                                            </CaptchaServiceContextProvider>
                                                                                        </PciProxyContextProvider>
                                                                                    </GainsightServiceProvider>
                                                                                </QueryClientProvider>
                                                                            </GraphqlClientConfigProvider>
                                                                        </RepositoryRegistryProvider>
                                                                    </AppClockServiceProvider>
                                                                </CommanderBreakpointRangeProvider>
                                                            </WindowSizeProvider>
                                                        </CommanderContextProvider>
                                                    </RouteResolveProvider>
                                                </LocalizationProvider>
                                            </GlobalizationProvider>
                                        </QueryBoundary>
                                    </IntlLocalizationProvider>
                                </DefaultErrorBoundary>
                            </CommanderObservability>
                        </SuspenseBoundary>
                    </OptimusProvider>
                </LoggerServiceProvider>
            </FeatureFlagServiceProvider>
        </Provider>,
        rootElement
    );
}

export const getMountElement = (elementId: string, componentType: ComponentType) => {
    const rootElement = document.getElementById(elementId) as HTMLElement;
    rootElement.setAttribute(REACT_COMPONENT_DATA, '');
    rootElement.setAttribute(COMPONENT_TYPE, componentType);

    return rootElement;
};

export async function getInitChainWithCheck() {
    checkIsRunning();

    const initChain = getInitChain();
    const { dispatch } = getStore();

    try {
        return await initChain;
    } catch (error) {
        dispatch(logSentryError({ error }));
    }
}
